import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Input, Modal, Switch } from '../ui';
import { cn } from '../../utils/cn';
import { getAxlCompetitors, searchStockCompanies } from '../../services/aiReportApi';

/** 1–10 chars, typical US ticker (fallback when name search is unavailable). */
const TICKER_LIKE = /^[A-Za-z0-9.-]{1,10}$/;

type SearchHit = { id: string; n: string };
type Competitor = { name: string; website?: string };
type CompetitorTiers = Record<string, Competitor[]>;

export type CompanyReportSelection = {
  ticker: string;
  companyName: string;
  includeGeneralUseCases: boolean;
  includeCompetitorUseCases: boolean;
  competitorTiers: string[];
  selectedCompetitors: Record<string, Competitor[]>;
  includeComparableEngagements: boolean;
};

type ReferenceCompanyTickerModalProps = {
  open: boolean;
  onClose: () => void;
  initialQuery: string;
  onComplete?: (selection: CompanyReportSelection | null) => void;
  onGenerate?: (selection: CompanyReportSelection | null, editable: boolean) => void;
};

const formatTier = (tier: string) =>
  tier.replace(/(\d+)/, ' $1').replace(/^./, (value) => value.toUpperCase());

const MAX_COMPANY_RESULTS = 4;

const NO_COMPETITOR_TIERS_MESSAGE =
  'No competitor tiers were found for this company. Change the name above and search again, or turn off competitor use cases.';

const ReferenceCompanyTickerModal: React.FC<ReferenceCompanyTickerModalProps> = ({
  open,
  onClose,
  initialQuery,
  onComplete,
  onGenerate,
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<SearchHit[]>([]);
  const [selected, setSelected] = useState<SearchHit | null>(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [includeMarketIntelligence, setIncludeMarketIntelligence] = useState(false);
  const [includeGeneralUseCases, setIncludeGeneralUseCases] = useState(false);
  const [includeCompetitorUseCases, setIncludeCompetitorUseCases] = useState(false);
  const [competitorTiers, setCompetitorTiers] = useState<CompetitorTiers>({});
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [includeComparableEngagements, setIncludeComparableEngagements] = useState(false);
  const [wantEditable, setWantEditable] = useState(false);
  const [referenceFeedback, setReferenceFeedback] = useState<string | null>(null);

  const resetAll = () => {
    setQuery(initialQuery);
    setResults([]);
    setSelected(null);
    setError(null);
    setNotice(null);
    setReferenceFeedback(null);
    setIncludeMarketIntelligence(false);
    setIncludeGeneralUseCases(false);
    setIncludeCompetitorUseCases(false);
    setCompetitorTiers({});
    setSelectedTiers([]);
    setIncludeComparableEngagements(false);
    setWantEditable(false);
  };

  useEffect(() => {
    if (open) resetAll();
  }, [open, initialQuery]);

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const finish = (selection: CompanyReportSelection | null) => {
    onComplete?.(selection);
    onGenerate?.(selection, wantEditable);
    handleClose();
  };

  const runSearch = async () => {
    const q = query.trim();
    if (!q) {
      setError('Enter a company name to search.');
      return;
    }

    setLoadingSearch(true);
    setError(null);
    setNotice(null);
    setSelected(null);
    setCompetitorTiers({});
    setSelectedTiers([]);
    setReferenceFeedback(null);
    try {
      const hits = await searchStockCompanies(q);
      setResults(hits);
      if (hits.length === 0) {
        if (TICKER_LIKE.test(q)) {
          await loadCompetitorsForCompany({ id: q.toUpperCase(), n: q.toUpperCase() });
          return;
        }
        setReferenceFeedback('No companies matched that search. Try a different name or ticker.');
      }
    } catch {
      if (TICKER_LIKE.test(q)) {
        try {
          await loadCompetitorsForCompany({ id: q.toUpperCase(), n: q.toUpperCase() });
          return;
        } catch {
          /* fall through */
        }
      }
      setReferenceFeedback(
        'Company search failed. Try a ticker symbol (e.g. GOOGL, MSFT) or search again.',
      );
      setResults([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const loadCompetitorsForCompany = async (hit: SearchHit) => {
    if (!hit.id) {
      return;
    }

    setSelected(hit);
    setLoadingCompetitors(true);
    setError(null);
    setNotice(null);
    setReferenceFeedback(null);
    setCompetitorTiers({});
    setSelectedTiers([]);
    try {
      const tiers = await getAxlCompetitors(hit.id);
      const available = Object.entries(tiers || {}).reduce<CompetitorTiers>((acc, [tier, competitors]) => {
        if (Array.isArray(competitors) && competitors.length > 0) acc[tier] = competitors;
        return acc;
      }, {});
      if (Object.keys(available).length === 0) {
        setResults([]);
        setReferenceFeedback(NO_COMPETITOR_TIERS_MESSAGE);
        return;
      }
      setCompetitorTiers(available);
      setResults([]);
      setReferenceFeedback(null);
    } catch {
      setResults([]);
      setReferenceFeedback(NO_COMPETITOR_TIERS_MESSAGE);
    } finally {
      setLoadingCompetitors(false);
    }
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setResults([]);
    setSelected(null);
    setCompetitorTiers({});
    setSelectedTiers([]);
    setNotice(null);
    setError(null);
    setReferenceFeedback(null);
  };

  const retryCompanySearch = () => {
    setReferenceFeedback(null);
    void runSearch();
  };

  const hasTierOptions = Object.keys(competitorTiers).length > 0;
  const showCompanyResults =
    results.length > 0 && !hasTierOptions && !loadingCompetitors && !referenceFeedback;
  const visibleCompanyResults = results.slice(0, MAX_COMPANY_RESULTS);
  const hiddenCompanyResultCount = Math.max(0, results.length - visibleCompanyResults.length);

  const selectCompany = (hit: SearchHit) => {
    void loadCompetitorsForCompany(hit);
  };

  const toggleCompetitors = (checked: boolean) => {
    setIncludeCompetitorUseCases(checked);
    setError(null);
    setNotice(null);
    setCompetitorTiers({});
    setSelectedTiers([]);
    setReferenceFeedback(null);
  };

  const toggleTier = (tier: string, checked: boolean) => {
    setSelectedTiers((current) =>
      checked ? [...current, tier] : current.filter((value) => value !== tier)
    );
  };

  const generateWithIntelligence = () => {
    if (includeMarketIntelligence && !includeGeneralUseCases && !includeCompetitorUseCases) {
      setError('Choose general or competitor use cases, or turn market intelligence off.');
      return;
    }
    if (includeMarketIntelligence && includeCompetitorUseCases && !selected?.id) {
      setError('Search and select a company for competitor use cases.');
      return;
    }
    if (includeMarketIntelligence && includeCompetitorUseCases && selectedTiers.length === 0) {
      setError('Select at least one competitor tier.');
      return;
    }

    const useCompetitorData = includeMarketIntelligence && includeCompetitorUseCases;
    finish({
      ticker: selected?.id || '',
      companyName: selected?.n || '',
      includeGeneralUseCases: includeMarketIntelligence && includeGeneralUseCases,
      includeCompetitorUseCases: useCompetitorData,
      competitorTiers: useCompetitorData ? selectedTiers : [],
      selectedCompetitors: useCompetitorData
        ? Object.fromEntries(selectedTiers.map((tier) => [tier, competitorTiers[tier] || []]))
        : {},
      includeComparableEngagements,
    });
  };

  const competitorReady =
    !includeMarketIntelligence || !includeCompetitorUseCases || selectedTiers.length > 0;
  const marketReady =
    !includeMarketIntelligence || includeGeneralUseCases || includeCompetitorUseCases;
  const canGenerate =
    (includeMarketIntelligence || includeComparableEngagements) &&
    marketReady &&
    competitorReady &&
    !loadingCompetitors;

  const hasEnrichment = includeMarketIntelligence || includeComparableEngagements;
  const showAsideSetup =
    includeMarketIntelligence ||
    (includeComparableEngagements && !includeMarketIntelligence);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="2xl"
      className="sme-report-config-modal"
      title="Generate report with AI"
      footer={
        <div className="sme-report-config-footer">
          <div className="sme-report-config-footer__actions">
            <Button
              variant="outline"
              size="sm"
              className="sme-report-config-footer__equal"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant={hasEnrichment ? 'secondary' : 'primary'}
              size="sm"
              className="sme-report-config-footer__equal"
              onClick={() => finish(null)}
            >
              Skip
            </Button>
            {hasEnrichment ? (
              <Button
                variant="primary"
                size="sm"
                className="sme-report-config-footer__primary"
                onClick={generateWithIntelligence}
                disabled={!canGenerate}
              >
                Generate with AI
              </Button>
            ) : null}
          </div>
          {!hasEnrichment ? (
            <p className="sme-report-config-footer__note">
              <strong>Skip</strong> generates the same full capability assessment report as before,
              without optional market intelligence or comparable engagement sections.
            </p>
          ) : null}
        </div>
      }
    >
      <div className="sme-report-config">
        <p className="sme-report-config__lede">
          Optional sections are off by default. Click <strong>Skip</strong> to generate the standard
          full report. Turn on enrichments below, finish setup on the right, then use{' '}
          <strong>Generate with AI</strong>.
        </p>

        <div className="sme-report-config__grid">
          <div className="sme-report-config__column sme-report-config__column--options">
            <section
              className="sme-report-config__panel sme-report-config__panel--options"
              aria-labelledby="sme-report-config-options"
            >
              <h3 id="sme-report-config-options" className="sme-report-config__panel-title">
                Optional enrichments
              </h3>
              <div className="sme-ticker-option-row">
                <div className="sme-ticker-option-row__text">
                  <span className="sme-ticker-option-row__title">Market intelligence</span>
                  <span className="sme-ticker-option-row__hint">
                    Industry use cases and competitor examples.
                  </span>
                </div>
                <Switch
                  className="sme-ticker-option-row__switch"
                  checked={includeMarketIntelligence}
                  onCheckedChange={(checked) => {
                    setIncludeMarketIntelligence(checked);
                    setError(null);
                    setNotice(null);
                  }}
                  aria-label="Include market intelligence"
                />
              </div>
              <div className="sme-ticker-option-row">
                <div className="sme-ticker-option-row__text">
                  <span className="sme-ticker-option-row__title">Comparable engagements</span>
                  <span className="sme-ticker-option-row__hint">
                    Adds comparable engagement content to the report.
                  </span>
                </div>
                <Switch
                  className="sme-ticker-option-row__switch"
                  checked={includeComparableEngagements}
                  onCheckedChange={(checked) => {
                    setIncludeComparableEngagements(checked);
                    setError(null);
                  }}
                  aria-label="Include comparable engagements"
                />
              </div>
            </section>

            <section
              className="sme-report-config__panel sme-report-config__panel--options"
              aria-labelledby="sme-report-config-output"
            >
              <h3 id="sme-report-config-output" className="sme-report-config__panel-title">
                Output format
              </h3>
              <div className="sme-ticker-option-row">
                <div className="sme-ticker-option-row__text">
                  <span className="sme-ticker-option-row__title">Editable Word document</span>
                  <span className="sme-ticker-option-row__hint">
                    Off produces a PDF. On produces an editable Word file.
                  </span>
                </div>
                <Switch
                  className="sme-ticker-option-row__switch"
                  checked={wantEditable}
                  onCheckedChange={setWantEditable}
                  aria-label="Want an editable Word document"
                />
              </div>
            </section>
          </div>

          <div className="sme-report-config__column sme-report-config__column--setup">
            {!showAsideSetup ? (
              <div className="sme-report-config__aside-empty" aria-hidden="true">
                <p className="m-0 font-semibold text-[var(--app-text)]">No setup needed</p>
                <p className="m-0 mt-2">
                  Enable market intelligence or comparable engagements to configure AI content here.
                </p>
              </div>
            ) : null}

            {includeComparableEngagements && !includeMarketIntelligence ? (
              <section className="sme-report-config__panel">
                <h3 className="sme-report-config__panel-title">Comparable engagements</h3>
                <p className="sme-report-config__panel-copy m-0">
                  No extra steps. Click <strong>Generate with AI</strong> in the footer when you are
                  ready.
                </p>
              </section>
            ) : null}

            {includeMarketIntelligence ? (
              <>
                <section className="sme-report-config__panel sme-report-config__panel--stack">
                  <h3 className="sme-report-config__panel-title">Market intelligence content</h3>
                  <p className="sme-report-config__panel-copy m-0">
                    Select at least one content type. Competitor use cases require a reference
                    company below.
                  </p>
                  <Checkbox
                    id="general-use-cases"
                    checked={includeGeneralUseCases}
                    onChange={(event) => setIncludeGeneralUseCases(event.target.checked)}
                    label="General use cases"
                    description="Industry and process examples."
                  />
                  <Checkbox
                    id="competitor-use-cases"
                    checked={includeCompetitorUseCases}
                    onChange={(event) => toggleCompetitors(event.target.checked)}
                    label="Competitor use cases"
                    description="Examples from selected competitor tiers."
                  />
                </section>

                {includeCompetitorUseCases ? (
                  <section className="sme-report-config__panel sme-report-config__panel--stack">
                    <h3 className="sme-report-config__panel-title">Reference company</h3>
                    <div className="sme-report-config__search-row">
                      <div className="sme-report-config__search-field">
                        <Input
                          label="Company name or ticker"
                          value={query}
                          onChange={(event) => handleQueryChange(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              void runSearch();
                            }
                          }}
                        />
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        className="sme-report-config__search-btn"
                        onClick={runSearch}
                        isLoading={loadingSearch}
                      >
                        Search
                      </Button>
                    </div>

                    {loadingCompetitors ? (
                      <div
                        className="sme-report-config__reference-slot sme-report-config__reference-slot--compact"
                        aria-busy="true"
                        aria-live="polite"
                      >
                        <p className="m-0 text-sm font-semibold text-[var(--app-text)]">
                          {selected?.n ?? 'Company'}
                        </p>
                        <p className="m-0 text-sm text-[var(--app-text-muted)]">
                          Loading competitor tiers…
                        </p>
                      </div>
                    ) : null}

                    {referenceFeedback && !loadingCompetitors && !hasTierOptions ? (
                      <div
                        className="sme-report-config__reference-slot sme-report-config__reference-slot--compact sme-report-config__reference-slot--feedback"
                        role="alert"
                        aria-live="assertive"
                      >
                        {selected ? (
                          <p className="sme-report-config__reference-company m-0">
                            {selected.n}{' '}
                            <span className="sme-report-config__ticker-badge">{selected.id}</span>
                          </p>
                        ) : null}
                        <Alert
                          variant="warning"
                          description={referenceFeedback}
                          className="sme-report-config__alert"
                        />
                        <div className="sme-report-config__reference-feedback-actions">
                          <Button variant="outline" size="sm" onClick={retryCompanySearch}>
                            Search again
                          </Button>
                        </div>
                      </div>
                    ) : null}

                    {hasTierOptions && selected ? (
                      <div className="sme-report-config__reference-slot sme-report-config__reference-slot--compact sme-report-config__reference-slot--tiers">
                        <div className="sme-report-config__reference-slot-head">
                          <p className="sme-report-config__panel-title m-0">Competitor tiers</p>
                          <p className="sme-report-config__reference-company m-0">
                            {selected.n}{' '}
                            <span className="sme-report-config__ticker-badge">{selected.id}</span>
                          </p>
                        </div>
                        <div className="sme-report-config__tiers">
                          {Object.entries(competitorTiers).map(([tier, competitors]) => (
                            <Checkbox
                              key={tier}
                              id={`competitor-tier-${tier}`}
                              checked={selectedTiers.includes(tier)}
                              onChange={(event) => toggleTier(tier, event.target.checked)}
                              label={formatTier(tier)}
                              description={`${competitors.length} competitor${competitors.length === 1 ? '' : 's'}`}
                            />
                          ))}
                        </div>
                        <p className="sme-report-config__reference-hint m-0">
                          To pick a different company, change the name above and search again.
                        </p>
                      </div>
                    ) : null}

                    {showCompanyResults ? (
                      <div className="sme-report-config__reference-slot sme-report-config__reference-slot--compact">
                        <p className="sme-ticker-search-results__heading m-0">
                          Select a company — tiers load automatically
                        </p>
                        {hiddenCompanyResultCount > 0 ? (
                          <p className="sme-report-config__reference-hint m-0">
                            Showing {visibleCompanyResults.length} of {results.length}. Refine
                            search to narrow results.
                          </p>
                        ) : null}
                        <ul
                          className="sme-ticker-search-results sme-ticker-search-results--embedded"
                          role="listbox"
                          aria-label="Company search results"
                        >
                          {visibleCompanyResults.map((hit) => {
                            const isSelected = selected?.id === hit.id;
                            return (
                              <li key={hit.id} role="presentation">
                                <button
                                  type="button"
                                  role="option"
                                  aria-selected={isSelected}
                                  className={cn(
                                    'sme-ticker-search-option',
                                    isSelected && 'sme-ticker-search-option--selected'
                                  )}
                                  onClick={() => selectCompany(hit)}
                                >
                                  <span className="min-w-0 flex-1">
                                    <span className="sme-ticker-search-option__name">{hit.n}</span>
                                  </span>
                                  <span className="sme-ticker-search-option__ticker">{hit.id}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : null}
                  </section>
                ) : null}
              </>
            ) : null}
          </div>
        </div>

        {notice ? <Alert variant="warning" description={notice} className="sme-report-config__alert" /> : null}
        {error ? <Alert variant="error" description={error} className="sme-report-config__alert" /> : null}
      </div>
    </Modal>
  );
};

export default ReferenceCompanyTickerModal;
